/**
 * Domains List Tab Component
 *
 * Displays list of domain sources for a project with filtering and actions
 */

import { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import {
  Table,
  Button,
  Space,
  Tag,
  Typography,
  message,
  Badge,
  Card,
  Popover,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Spin,
  Tooltip,
} from 'antd'
import {
  ReloadOutlined,
  UnlockOutlined,
  SyncOutlined,
  SearchOutlined,
  GlobalOutlined,
  FileTextOutlined,
  CodeOutlined,
  BookOutlined,
  ToolOutlined,
  PhoneOutlined,
  FilterOutlined,
  ClearOutlined,
} from '@ant-design/icons'
import type { ColumnsType, TablePaginationConfig, SorterResult } from 'antd/es/table/interface'
import api from '@/services/api'
import { useAuthStore } from '@/stores/authStore'

const { Title, Text } = Typography
const { TextArea } = Input

interface DomainsListTabProps {
  projectId: number
}

interface Source {
  id: number
  project_id: number
  vdl_id: number | null
  source: string
  source_type: string
  subsource: string | null
  source_from: string | null
  status: string
  will_work: boolean
  ai_score: number | null
  ai_comment: string | null
  locked_by_operator_id: number | null
  locked_at: string | null
  created_at: string
}

interface StatusHistoryItem {
  id: number
  source_id: number
  operator_id: number | null
  operator_username: string | null
  old_status: string | null
  new_status: string
  rejected_reason: string | null
  comment: string | null
  created_at: string
}

interface Project {
  id: number
  crm_id: number
  project_name: string
  status: string
  parse_domains: boolean
  parse_phones: boolean
  parse_manual: boolean
  use_mg: boolean
  vdl_autonorms: boolean
  default_limit: number
  max_limit: number
  total_sources: number
  active_sources: number
  inactive_sources: number
  pending_sources: number
  created_at: string
  updated_at: string
  last_sync_at: string | null
  last_collection_run_at: string | null
  config: string | null
}

const statusColors: Record<string, string> = {
  pending: 'warning',
  pre_approved: 'processing',
  active: 'success',
  inactive: 'default',
  deleted: 'error',
}

const statusLabels: Record<string, string> = {
  pending: 'Ожидание',
  pre_approved: 'Предварительно одобрен',
  active: 'Одобрен',
  inactive: 'Неактивен',
  deleted: 'Удален',
}

const DomainsListTab = forwardRef(({ projectId }: DomainsListTabProps, ref) => {
  const { operator } = useAuthStore()
  
  const [project, setProject] = useState<Project | null>(null)
  const [sources, setSources] = useState<Source[]>([])
  const [loading, setLoading] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [lockedSources, setLockedSources] = useState<Source[]>([])
  const [statistics, setStatistics] = useState<{
    active_count: number
    inactive_count: number
    pending_count?: number
    pre_approved_count?: number
    total_domains_count?: number
    available_domains_count?: number
    can_generate_config: boolean
  } | null>(null)
  const [queriesRegions, setQueriesRegions] = useState<{
    queries: string[]
    regions: Array<{ id: number; name: string }>
    queries_count: number
    regions_count: number
  } | null>(null)
  const [script, setScript] = useState<any>(null)
  const [loadingScript, setLoadingScript] = useState(false)
  const [vdlTz, setVdlTz] = useState<string | null>(null)
  const [loadingVdlTz, setLoadingVdlTz] = useState(false)
  
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  })
  
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>(undefined)

  // * Filters
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined)
  const [aiScoreMin, setAiScoreMin] = useState<number | undefined>(undefined)
  const [aiScoreMax, setAiScoreMax] = useState<number | undefined>(undefined)
  const [filterVersion, setFilterVersion] = useState(0)

  const [domainSourcesCount, setDomainSourcesCount] = useState<number>(0)
  const [aiConfig, setAiConfig] = useState<any>(null)
  const [loadingAiConfig, setLoadingAiConfig] = useState(false)
  
  // * Admin modal state
  const [adminModalVisible, setAdminModalVisible] = useState(false)
  const [adminModalSource, setAdminModalSource] = useState<Source | null>(null)
  const [adminStatusForm] = Form.useForm()
  const [updatingAdminStatus, setUpdatingAdminStatus] = useState(false)
  
  // * Check if current operator is admin or manager
  const isAdmin = operator?.role === 'admin' || operator?.role === 'manager'

  // * Status history state
  const [statusHistoryCache, setStatusHistoryCache] = useState<Record<number, StatusHistoryItem[]>>({})
  const [loadingHistory, setLoadingHistory] = useState<Record<number, boolean>>({})

  // * Auto-refetch when filters change
  useEffect(() => {
    if (projectId && filterVersion > 0) {
      fetchSources(1, pagination.pageSize)
    }
  }, [statusFilter, filterVersion])

  // * Phone extraction state
  const [extractingPhones, setExtractingPhones] = useState<Record<number, boolean>>({})

  const fetchProject = async () => {
    if (!projectId) return
    
    try {
      const data = await api.getProject(projectId)
      setProject(data)
    } catch (error) {
      console.error('Fetch project error:', error)
      message.error('Ошибка загрузки проекта')
    }
  }

  const syncProjectSources = async () => {
    if (!projectId) return
    
    setSyncing(true)
    
    try {
      const result = await api.syncProjectSources(projectId)
      console.log('Синхронизация завершена:', result)
      message.success(
        `Синхронизация: +${result.created} новых, ~${result.updated} обновлено, -${result.deleted} удалено`,
        5
      )
      
      await fetchProject()
      await fetchSources(1, pagination.pageSize)
      await fetchQueriesRegions()
      setSortField(undefined)
      setSortOrder(undefined)
    } catch (error) {
      console.error('Sync project error:', error)
      message.error('Ошибка синхронизации с VDL API')
    } finally {
      setSyncing(false)
    }
  }

  const fetchSources = async (
    page: number = 1, 
    pageSize: number = 20, 
    sortFieldParam?: string | undefined, 
    sortOrderParam?: 'asc' | 'desc' | undefined
  ) => {
    if (!projectId) return
    
    setLoading(true)
    
    try {
      const response = await api.getSources({
        project_id: projectId,
        page,
        page_size: pageSize,
        source_type: 'domain',
        status: statusFilter,
        ai_score_min: aiScoreMin,
        ai_score_max: aiScoreMax,
        sort_by: sortFieldParam !== undefined ? sortFieldParam : sortField,
        sort_order: sortOrderParam !== undefined ? sortOrderParam : sortOrder,
      })
      
      setSources(response.items)
      setDomainSourcesCount(response.total)
      
      setPagination({
        current: response.page,
        pageSize: response.page_size,
        total: response.total,
      })
    } catch (error) {
      console.error('Fetch sources error:', error)
      message.error('Ошибка загрузки источников')
    } finally {
      setLoading(false)
    }
  }

  const fetchLockedSources = async () => {
    if (!projectId) return
    
    try {
      const response = await api.getMyLockedSources(projectId)
      setLockedSources(response.sources)
    } catch (error) {
      console.error('Fetch locked sources error:', error)
    }
  }

  const fetchStatistics = async () => {
    if (!projectId) return
    
    try {
      const stats = await api.getProjectStatistics(projectId)
      setStatistics(stats)
    } catch (error) {
      console.error('Fetch statistics error:', error)
    }
  }

  const fetchQueriesRegions = async () => {
    if (!projectId) return
    
    try {
      const data = await api.getProjectQueriesRegions(projectId)
      setQueriesRegions(data)
    } catch (error) {
      console.error('Fetch queries and regions error:', error)
    }
  }

  const fetchScript = async () => {
    if (!projectId) return
    
    setLoadingScript(true)
    try {
      const data = await api.getProjectScript(projectId)
      if (typeof data.script === 'string') {
        setScript(data.script)
      } else if (data.script && typeof data.script === 'object') {
        const scriptHtml = data.script.html || data.script.content || data.script.script || JSON.stringify(data.script, null, 2)
        setScript(scriptHtml)
      } else {
        setScript(null)
      }
    } catch (error: any) {
      if (error.response?.status !== 404) {
        console.error('Fetch script error:', error)
      }
      setScript(null)
    } finally {
      setLoadingScript(false)
    }
  }

  const fetchVdlTz = async () => {
    if (!projectId) return
    
    setLoadingVdlTz(true)
    try {
      const data = await api.getVdlTz(projectId)
      setVdlTz(data.vdl_tz)
    } catch (error: any) {
      if (error.response?.status !== 404) {
        console.error('Fetch VDL TZ error:', error)
      }
      setVdlTz(null)
    } finally {
      setLoadingVdlTz(false)
    }
  }

  const handleGenerateConfig = async () => {
    if (!projectId) return
    
    try {
      const result = await api.generateProjectConfig(projectId)
      message.success('AI-конфиг успешно сгенерирован и сохранен!', 5)
      
      setAiConfig(result.config)
      
      await fetchProject()
      await fetchStatistics()
    } catch (error: any) {
      console.error('Generate config error:', error)
      message.error(error.response?.data?.detail || 'Ошибка генерации конфига')
    }
  }

  const fetchAiConfig = async () => {
    if (!projectId) return
    
    setLoadingAiConfig(true)
    try {
      const config = await api.getProjectAIConfig(projectId)
      setAiConfig(config)
    } catch (error: any) {
      if (error.response?.status !== 404) {
        console.error('Fetch AI config error:', error)
      }
      setAiConfig(null)
    } finally {
      setLoadingAiConfig(false)
    }
  }

  const handleReleaseSource = async (sourceId: number) => {
    try {
      await api.releaseSource(sourceId)
      message.success('Источник освобожден')
      await fetchLockedSources()
      await fetchSources(pagination.current, pagination.pageSize)
    } catch (error) {
      console.error('Release source error:', error)
      message.error('Ошибка освобождения источника')
    }
  }
  
  // * Admin: Open status edit modal
  const handleAdminOpenModal = (source: Source) => {
    setAdminModalSource(source)
    adminStatusForm.setFieldsValue({
      status: source.status,
      comment: '',
    })
    setAdminModalVisible(true)
  }
  
  // * Admin: Submit status change
  const handleAdminStatusSubmit = async () => {
    if (!adminModalSource) return
    
    try {
      setUpdatingAdminStatus(true)
      const values = await adminStatusForm.validateFields()
      
      await api.adminUpdateSourceStatus(adminModalSource.id, {
        status: values.status,
        comment: values.comment,
      })
      
      message.success(`Статус источника ${adminModalSource.source} успешно обновлен`)
      setAdminModalVisible(false)
      setAdminModalSource(null)
      adminStatusForm.resetFields()
      
      // * Refresh sources list
      await fetchSources(pagination.current, pagination.pageSize)
    } catch (error: any) {
      console.error('Admin status update error:', error)
      
      if (error.response?.status === 403) {
        message.error('Недостаточно прав для выполнения операции')
      } else {
        message.error(error.response?.data?.detail || 'Ошибка обновления статуса')
      }
    } finally {
      setUpdatingAdminStatus(false)
    }
  }

  // * Handle phone extraction for a source
  const handleExtractPhones = async (source: Source) => {
    if (extractingPhones[source.id]) return

    setExtractingPhones(prev => ({ ...prev, [source.id]: true }))

    try {
      const result = await api.extractPhonesFromSource({
        source_id: source.id,
        methods: ['quick', 'phone_parser'],
      })
      message.success(`Сбор телефонов запущен для ${source.source} (${result.parsed_urls_count} страниц)`)
    } catch (error: any) {
      console.error('Extract phones error:', error)
      const detail = error.response?.data?.detail || ''
      if (detail.includes('already completed')) {
        message.info(`Телефоны уже собраны для домена ${source.source}`)
      } else if (detail.includes('already running')) {
        message.info(`Сбор телефонов уже запущен для домена ${source.source}`)
      } else if (detail.includes('No pages pending')) {
        message.info(`Нет страниц для сбора телефонов на домене ${source.source}`)
      } else if (detail.includes('No approved pages')) {
        message.warning(`Нет одобренных страниц для домена ${source.source}`)
      } else {
        message.error(detail || 'Ошибка запуска сбора телефонов')
      }
    } finally {
      setExtractingPhones(prev => ({ ...prev, [source.id]: false }))
    }
  }

  const handleTableChange = (
    newPagination: TablePaginationConfig,
    _filters: any,
    sorter: SorterResult<Source> | SorterResult<Source>[]
  ) => {
    const singleSorter = !Array.isArray(sorter) ? sorter : undefined
    const newField = singleSorter?.columnKey as string | undefined
    const newOrder = singleSorter?.order === 'ascend' ? 'asc' : singleSorter?.order === 'descend' ? 'desc' : undefined

    // Check if sorting actually changed (not just pagination click with active sort)
    const sortChanged = (newOrder ? newField : undefined) !== sortField || newOrder !== sortOrder

    if (sortChanged) {
      setSortField(newOrder ? newField : undefined)
      setSortOrder(newOrder)

      setPagination({
        ...pagination,
        current: 1,
        pageSize: newPagination.pageSize || pagination.pageSize,
      })
      fetchSources(1, newPagination.pageSize || pagination.pageSize, newOrder ? newField : undefined, newOrder)
    } else {
      setPagination({
        ...pagination,
        current: newPagination.current || 1,
        pageSize: newPagination.pageSize || pagination.pageSize,
      })
      fetchSources(newPagination.current || 1, newPagination.pageSize || pagination.pageSize)
    }
  }

  // * Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    triggerSync: async () => {
      await syncProjectSources()
    },
    triggerGenerateConfig: async () => {
      await handleGenerateConfig()
    },
  }))

  useEffect(() => {
    const initializePage = async () => {
      await fetchProject()
      // * Load table data immediately (don't wait for sync)
      await Promise.all([
        fetchSources(),
        fetchLockedSources(),
        fetchStatistics(),
        fetchQueriesRegions(),
        fetchAiConfig(),
        fetchScript(),
        fetchVdlTz(),
      ])
      // * Auto sync with VDL in background, then refresh table
      syncProjectSources()
    }

    initializePage()

    const interval = setInterval(fetchLockedSources, 10000)

    return () => clearInterval(interval)
  }, [projectId])

  const columns: ColumnsType<Source> = [
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>Источник</span>,
      dataIndex: 'source',
      key: 'source',
      ellipsis: true,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (text: string, record: Source) => {
        const isDomain = record.source_type === 'domain'
        const url = text.startsWith('http') ? text : `https://${text}`

        return isDomain ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: '13px', color: '#1890ff' }}
          >
            {text}
          </a>
        ) : (
          <span style={{ fontSize: '13px', color: '#262626' }}>{text}</span>
        )
      },
    },
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>Тип</span>,
      dataIndex: 'source_type',
      key: 'source_type',
      width: 80,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (type: string) => (
        <Tag 
          color="default" 
          style={{ 
            margin: 0,
            fontSize: '11px',
            padding: '1px 6px',
            height: '20px',
            lineHeight: '18px',
          }}
        >
          {type}
        </Tag>
      ),
    },
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>Подисточник</span>,
      dataIndex: 'subsource',
      key: 'subsource',
      ellipsis: true,
      width: 150,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (text: string | null, record: Source) => {
        if (!text || text === record.source) return <span style={{ color: '#bfbfbf', fontSize: '12px' }}>—</span>
        return <span style={{ fontSize: '13px', color: '#595959' }}>{text}</span>
      },
    },
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>AI Балл</span>,
      dataIndex: 'ai_score',
      key: 'ai_score',
      width: 80,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (score: number | null, record: Source) => {
        if (score == null) return <span style={{ color: '#bfbfbf', fontSize: '12px' }}>—</span>
        const color = score >= 70 ? '#52c41a' : score >= 40 ? '#faad14' : '#ff4d4f'
        const scoreEl = (
          <span style={{ fontSize: '13px', fontWeight: 600, color, cursor: record.ai_comment ? 'help' : 'default' }}>
            {score}
          </span>
        )
        if (record.ai_comment) {
          return (
            <Tooltip title={record.ai_comment} placement="topLeft" overlayStyle={{ maxWidth: 400 }}>
              {scoreEl}
            </Tooltip>
          )
        }
        return scoreEl
      },
    },
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>Статус</span>,
      dataIndex: 'status',
      key: 'status',
      width: 160,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (status: string, record: Source) => {
        const history = statusHistoryCache[record.id] || []
        const isLoading = loadingHistory[record.id] || false

        // * Добавляем начальный статус с датой создания источника в конец истории
        // * Берем old_status первой записи (самое раннее изменение) как начальный статус
        const fullHistory = history.length > 0
          ? (() => {
              const firstHistoryItem = history[history.length - 1] // последний в массиве = самый ранний по времени
              const initialStatus = firstHistoryItem.old_status

              // * Добавляем начальный статус только если он есть (old_status !== null)
              if (initialStatus) {
                return [...history, {
                  id: -1,
                  source_id: record.id,
                  operator_id: null,
                  operator_username: null,
                  old_status: null,
                  new_status: initialStatus,
                  rejected_reason: null,
                  comment: null,
                  created_at: record.created_at,
                }]
              }
              return history
            })()
          : []

        const tooltipContent = isLoading ? (
          <div style={{ backgroundColor: '#fff', padding: '8px' }}>
            <Spin size="small" />
          </div>
        ) : history.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#fff', padding: '4px' }}>
            <Tag
              color={statusColors[status] || 'default'}
              style={{ margin: 0, fontSize: '11px', padding: '2px 6px' }}
            >
              {statusLabels[status] || status}
            </Tag>
            <span style={{ fontSize: '12px', color: '#595959' }}>
              создан {formatDate(record.created_at)}
            </span>
          </div>
        ) : (
          <div style={{ maxWidth: '400px', maxHeight: '400px', overflowY: 'auto', backgroundColor: '#fff', padding: '4px' }}>
            {fullHistory.map((item, index) => (
              <div
                key={item.id}
                style={{
                  marginBottom: index < fullHistory.length - 1 ? '6px' : 0,
                  paddingBottom: index < fullHistory.length - 1 ? '6px' : 0,
                  borderBottom: index < fullHistory.length - 1 ? '1px solid #f0f0f0' : 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                  <Tag
                    color={statusColors[item.new_status] || 'default'}
                    style={{ margin: 0, fontSize: '11px', padding: '2px 6px' }}
                  >
                    {statusLabels[item.new_status] || item.new_status}
                  </Tag>
                  <span style={{ color: '#8c8c8c' }}>{formatDate(item.created_at)}</span>
                  {item.operator_username ? (
                    <span style={{ color: '#1890ff' }}>@{item.operator_username}</span>
                  ) : item.id === -1 ? (
                    <span style={{ color: '#8c8c8c', fontSize: '11px', fontStyle: 'italic' }}>(создан)</span>
                  ) : null}
                </div>
                {item.rejected_reason && (
                  <div style={{ fontSize: '11px', color: '#ff4d4f', marginTop: '4px', marginLeft: '4px' }}>
                    Причина: {item.rejected_reason}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
        
        return (
          <Popover
            content={tooltipContent}
            title="История статусов"
            placement="topLeft"
            trigger="hover"
            onOpenChange={(open) => {
              if (open) {
                loadStatusHistory(record.id)
              }
            }}
            overlayInnerStyle={{
              backgroundColor: '#fff',
              color: '#262626',
            }}
            overlayStyle={{ maxWidth: '350px' }}
          >
            <Tag 
              color={statusColors[status] || 'default'}
              style={{ 
                margin: 0,
                fontSize: '11px',
                padding: '2px 8px',
                height: '22px',
                lineHeight: '18px',
                cursor: 'help',
              }}
            >
              {statusLabels[status] || status}
            </Tag>
          </Popover>
        )
      },
    },
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>Заблокирован</span>,
      key: 'locked',
      width: 120,
      render: (_: any, record: Source) => {
        if (!record.locked_by_operator_id) {
          return <span style={{ color: '#bfbfbf', fontSize: '12px' }}>—</span>
        }
        
        const isMyLock = record.locked_by_operator_id === operator?.id
        
        return (
          <Space size={4}>
            <Badge 
              status={isMyLock ? 'success' : 'default'} 
              style={{ fontSize: '8px' }}
            />
            <Text 
              type={isMyLock ? 'success' : 'secondary'}
              style={{ fontSize: '12px' }}
            >
              {isMyLock ? 'Вы' : `ID ${record.locked_by_operator_id}`}
            </Text>
          </Space>
        )
      },
    },
    {
      title: <span style={{ fontSize: '12px', fontWeight: 500 }}>Действия</span>,
      key: 'actions',
      width: isAdmin ? 280 : 180,
      render: (_: any, record: Source) => {
        const isActive = record.status === 'active'
        const isExtracting = extractingPhones[record.id]

        return (
          <Space size="small">
            {/* * Phone extraction button for active domains */}
            {isActive && (
              <Button
                size="small"
                type="primary"
                icon={<PhoneOutlined />}
                onClick={() => handleExtractPhones(record)}
                loading={isExtracting}
                style={{
                  height: '24px',
                  fontSize: '11px',
                  padding: '0 8px',
                }}
                title="Собрать телефоны с домена"
              >
                Телефоны
              </Button>
            )}
            {record.locked_by_operator_id === operator?.id && (
              <Button
                size="small"
                icon={<UnlockOutlined />}
                onClick={() => handleReleaseSource(record.id)}
                style={{
                  height: '24px',
                  fontSize: '11px',
                  padding: '0 8px',
                }}
              >
                Освободить
              </Button>
            )}
            {isAdmin && (
              <Button
                size="small"
                type="dashed"
                icon={<ToolOutlined />}
                onClick={() => handleAdminOpenModal(record)}
                style={{
                  height: '24px',
                  fontSize: '11px',
                  padding: '0 8px',
                }}
                title="Изменить статус вручную"
              >
                Статус
              </Button>
            )}
          </Space>
        )
      },
    },
  ]

  // * Load status history for a source
  const loadStatusHistory = async (sourceId: number) => {
    // * Check if already cached
    if (statusHistoryCache[sourceId]) {
      return
    }
    
    // * Check if already loading
    if (loadingHistory[sourceId]) {
      return
    }
    
    setLoadingHistory(prev => ({ ...prev, [sourceId]: true }))
    
    try {
      const response = await api.getSourceStatusHistory(sourceId)
      setStatusHistoryCache(prev => ({
        ...prev,
        [sourceId]: response.items || []
      }))
    } catch (error) {
      console.error('Error loading status history:', error)
      // * Set empty array on error to prevent retries
      setStatusHistoryCache(prev => ({
        ...prev,
        [sourceId]: []
      }))
    } finally {
      setLoadingHistory(prev => {
        const newState = { ...prev }
        delete newState[sourceId]
        return newState
      })
    }
  }
  
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '—'
    try {
      let date: Date
      if (dateString.includes('Z') || dateString.match(/[+-]\d{2}:\d{2}$/)) {
        date = new Date(dateString)
      } else if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
        date = new Date(dateString + 'Z')
      } else {
        date = new Date(dateString)
      }
      
      if (isNaN(date.getTime())) return '—'
      
      return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    } catch (error) {
      console.error('Date formatting error:', error)
      return '—'
    }
  }

  const getProjectStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      active: 'Активен',
      test_active: 'Тест активен',
      pause: 'Пауза',
      stop: 'Остановлен',
      test_stop: 'Тест остановлен',
    }
    return statusMap[status] || status
  }

  const getProjectStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      active: 'success',
      test_active: 'processing',
      pause: 'warning',
      stop: 'default',
      test_stop: 'default',
    }
    return colorMap[status] || 'default'
  }

  return (
    <div>
      {/* * Unified compact header with integrated stats */}
      <Card 
        style={{ 
          marginBottom: 16,
          borderRadius: 6,
          border: '1px solid #e8e8e8',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        }}
        bodyStyle={{ padding: 0 }}
      >
        {/* * Top bar: Project info and actions */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          padding: '14px 20px',
          borderBottom: '1px solid #f0f0f0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Title level={5} style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>
              {project?.project_name || 'Загрузка...'}
            </Title>
            {project && (
              <Tag 
                color={getProjectStatusColor(project.status)}
                style={{ margin: 0, fontSize: '11px', padding: '1px 8px' }}
              >
                {getProjectStatusLabel(project.status)}
              </Tag>
            )}
            {syncing && (
              <Tag color="processing" icon={<SyncOutlined spin />} style={{ margin: 0, fontSize: '11px' }}>
                Синхронизация...
              </Tag>
            )}
            {project && (
              <Text type="secondary" style={{ fontSize: '12px', marginLeft: 8 }}>
                CRM ID: {project.crm_id}
              </Text>
            )}
          </div>

          <Space size="small">
            {/* * Local refresh button */}
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={async () => {
                await fetchProject()
                await fetchSources(pagination.current, pagination.pageSize)
                await fetchStatistics()
              }}
              loading={loading}
              title="Обновить данные таблицы"
            >
              Обновить
            </Button>
            
          </Space>
        </div>

        {/* * Stats bar: inline metrics */}
        <div style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 20px',
          backgroundColor: '#fafafa',
        }}>
          <Space size={24}>
            <div>
              <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: 2 }}>Всего</Text>
              <Text strong style={{ fontSize: '18px', color: '#262626' }}>
                {statistics?.total_domains_count ?? (project?.total_sources || 0)}
              </Text>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: 2 }}>Ожидают</Text>
              <Text strong style={{ fontSize: '18px', color: '#fa8c16' }}>
                {statistics?.pending_count ?? project?.pending_sources ?? 0}
              </Text>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: 2 }}>Предодобрены</Text>
              <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>
                {statistics?.pre_approved_count || 0}
              </Text>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: 2 }}>Активны</Text>
              <Text strong style={{ fontSize: '18px', color: '#52c41a' }}>
                {statistics?.active_count || 0}
              </Text>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: 2 }}>Неактивны</Text>
              <Text strong style={{ fontSize: '18px', color: '#8c8c8c' }}>
                {statistics?.inactive_count || 0}
              </Text>
            </div>
            {lockedSources.length > 0 && (
              <div>
                <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: 2 }}>Заблокировано мной</Text>
                <Text strong style={{ fontSize: '18px', color: '#722ed1' }}>
                  {lockedSources.length}
                </Text>
              </div>
            )}
          </Space>

          {/* * Webmaster parsing status and controls */}
          <Space size={16}>
            {/* * AI Config popover */}
            {aiConfig && (
              <Popover
                title="AI Конфигурация"
                content={
                  <div style={{ maxWidth: 700, maxHeight: 600, overflowY: 'auto', padding: '8px' }}>
                    <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                      {aiConfig.description && (
                        <div style={{ marginBottom: 16 }}>
                          <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: 4 }}>Описание:</Text>
                          <Text style={{ fontSize: '12px', color: '#595959' }}>{aiConfig.description}</Text>
                        </div>
                      )}
                      
                      {aiConfig.niche && (
                        <div style={{ marginBottom: 16 }}>
                          <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: 4 }}>Ниша:</Text>
                          <Tag color="blue" style={{ fontSize: '11px' }}>{aiConfig.niche}</Tag>
                        </div>
                      )}
                      
                      {aiConfig.target_audiences && aiConfig.target_audiences.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                          <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: 6 }}>Целевая аудитория:</Text>
                          <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            {aiConfig.target_audiences.map((audience: string, idx: number) => (
                              <Text key={idx} style={{ fontSize: '12px', color: '#595959', display: 'block' }}>
                                • {audience}
                              </Text>
                            ))}
                          </Space>
                        </div>
                      )}
                      
                      {aiConfig.focus_keywords && aiConfig.focus_keywords.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                          <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: 6 }}>Фокус-слова ({aiConfig.focus_keywords.length}):</Text>
                          <Space wrap size={4}>
                            {aiConfig.focus_keywords.slice(0, 20).map((keyword: string, idx: number) => (
                              <Tag key={idx} color="purple" style={{ fontSize: '11px', marginBottom: 4 }}>
                                {keyword}
                              </Tag>
                            ))}
                            {aiConfig.focus_keywords.length > 20 && (
                              <Text type="secondary" style={{ fontSize: '11px' }}>
                                +{aiConfig.focus_keywords.length - 20} еще
                              </Text>
                            )}
                          </Space>
                        </div>
                      )}
                      
                      {aiConfig.business_keywords && aiConfig.business_keywords.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                          <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: 6 }}>Бизнес-ключи ({aiConfig.business_keywords.length}):</Text>
                          <Space wrap size={4}>
                            {aiConfig.business_keywords.map((keyword: string, idx: number) => (
                              <Tag key={idx} color="green" style={{ fontSize: '11px', marginBottom: 4 }}>
                                {keyword}
                              </Tag>
                            ))}
                          </Space>
                        </div>
                      )}
                      
                      {aiConfig.exclude_keywords && aiConfig.exclude_keywords.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                          <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: 6 }}>Исключения ({aiConfig.exclude_keywords.length}):</Text>
                          <Space wrap size={4}>
                            {aiConfig.exclude_keywords.map((keyword: string, idx: number) => (
                              <Tag key={idx} color="red" style={{ fontSize: '11px', marginBottom: 4 }}>
                                {keyword}
                              </Tag>
                            ))}
                          </Space>
                        </div>
                      )}
                      
                      {aiConfig.cta_keywords && aiConfig.cta_keywords.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                          <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: 6 }}>CTA-ключи ({aiConfig.cta_keywords.length}):</Text>
                          <Space wrap size={4}>
                            {aiConfig.cta_keywords.map((keyword: string, idx: number) => (
                              <Tag key={idx} color="orange" style={{ fontSize: '11px', marginBottom: 4 }}>
                                {keyword}
                              </Tag>
                            ))}
                          </Space>
                        </div>
                      )}
                      
                      {aiConfig.created_at && (
                        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
                          <Text type="secondary" style={{ fontSize: '11px' }}>
                            Создано: {new Date(aiConfig.created_at).toLocaleString('ru-RU')}
                          </Text>
                        </div>
                      )}
                    </div>
                  </div>
                }
                trigger="click"
              >
                <Button 
                  size="small" 
                  icon={<FileTextOutlined />} 
                  style={{ fontSize: '11px' }}
                  loading={loadingAiConfig}
                >
                  AI Конфиг
                </Button>
              </Popover>
            )}
            
            {/* * Queries and Regions inline */}
            {(queriesRegions && (queriesRegions.queries_count > 0 || queriesRegions.regions_count > 0)) && (
              <>
                {queriesRegions.queries_count > 0 && (
                  <Popover
                    title={`Запросы (${queriesRegions.queries_count})`}
                    content={
                      <div style={{ maxWidth: 400, maxHeight: 300, overflowY: 'auto' }}>
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                          {queriesRegions.queries.map((query, index) => (
                            <Tag key={index} color="default" style={{ marginBottom: 4, fontSize: '11px' }}>
                              {query}
                            </Tag>
                          ))}
                        </Space>
                      </div>
                    }
                    trigger="click"
                  >
                    <Button size="small" icon={<SearchOutlined />} style={{ fontSize: '11px' }}>
                      {queriesRegions.queries_count} запросов
                    </Button>
                  </Popover>
                )}
                {queriesRegions.regions_count > 0 && (
                  <Popover
                    title={`Регионы (${queriesRegions.regions_count})`}
                    content={
                      <div style={{ maxWidth: 400, maxHeight: 300, overflowY: 'auto' }}>
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                          {queriesRegions.regions.map((region) => (
                            <Tag key={region.id || region.name} color="default" style={{ marginBottom: 4, fontSize: '11px' }}>
                              {region.id ? `${region.id} | ${region.name}` : region.name}
                            </Tag>
                          ))}
                        </Space>
                      </div>
                    }
                    trigger="click"
                  >
                    <Button size="small" icon={<GlobalOutlined />} style={{ fontSize: '11px' }}>
                      {queriesRegions.regions_count} регионов
                    </Button>
                  </Popover>
                )}
              </>
            )}
            
            {/* * Script and VDL TZ inline */}
            {script && (
              <Popover
                title="Скрипт"
                content={
                  <div style={{ maxWidth: 600, maxHeight: 400, overflowY: 'auto' }}>
                    <div 
                      dangerouslySetInnerHTML={{ __html: typeof script === 'string' ? script : String(script) }} 
                      style={{ 
                        fontSize: '12px', 
                        lineHeight: '1.6',
                        color: '#262626'
                      }}
                    />
                  </div>
                }
                trigger="click"
              >
                <Button 
                  size="small" 
                  icon={<CodeOutlined />} 
                  style={{ fontSize: '11px' }}
                  loading={loadingScript}
                >
                  Скрипт
                </Button>
              </Popover>
            )}
            {vdlTz && (
              <Popover
                title="ТЗ"
                content={
                  <div style={{ maxWidth: 600, maxHeight: 400, overflowY: 'auto' }}>
                    <Text style={{ fontSize: '12px', lineHeight: '1.6', color: '#262626', whiteSpace: 'pre-wrap' }}>
                      {vdlTz}
                    </Text>
                  </div>
                }
                trigger="click"
              >
                <Button 
                  size="small" 
                  icon={<BookOutlined />} 
                  style={{ fontSize: '11px' }}
                  loading={loadingVdlTz}
                >
                  ТЗ
                </Button>
              </Popover>
            )}
          </Space>
        </div>

        {/* * Project metadata - collapsible */}
        {project && (project.last_sync_at || project.last_collection_run_at || project.parse_domains || project.use_mg) && (
          <div style={{ 
            padding: '8px 20px',
            borderTop: '1px solid #f0f0f0',
            backgroundColor: '#fff',
          }}>
            <Space size={16} wrap style={{ fontSize: '11px', color: '#8c8c8c' }}>
              {project.last_sync_at && (
                <span>Синхр.: {formatDate(project.last_sync_at)}</span>
              )}
              {project.last_collection_run_at && (
                <span>Синхр. VDL: {formatDate(project.last_collection_run_at)}</span>
              )}
              {(project.parse_domains || project.parse_phones || project.parse_manual) && (
                <Space size={4}>
                  <span>Парсинг:</span>
                  {project.parse_domains && <Tag bordered={false} style={{ fontSize: '10px', padding: '0 4px', height: '16px', lineHeight: '16px' }}>домены</Tag>}
                  {project.parse_phones && <Tag bordered={false} style={{ fontSize: '10px', padding: '0 4px', height: '16px', lineHeight: '16px' }}>телефоны</Tag>}
                  {project.parse_manual && <Tag bordered={false} color="warning" style={{ fontSize: '10px', padding: '0 4px', height: '16px', lineHeight: '16px' }}>ручной</Tag>}
                </Space>
              )}
              {(project.default_limit > 0 || project.max_limit > 0) && (
                <span>
                  Лимиты: {project.default_limit > 0 && `${project.default_limit}`}{project.default_limit > 0 && project.max_limit > 0 && ' / '}{project.max_limit > 0 && `${project.max_limit}`}
                </span>
              )}
              {project.use_mg && <Tag bordered={false} style={{ fontSize: '10px', padding: '0 4px', height: '16px', lineHeight: '16px' }}>MG</Tag>}
              {project.vdl_autonorms && <Tag bordered={false} color="processing" style={{ fontSize: '10px', padding: '0 4px', height: '16px', lineHeight: '16px' }}>VDL автонормы</Tag>}
            </Space>
          </div>
        )}
      </Card>

      {/* * Filters */}
      <Card
        style={{
          marginBottom: 12,
          borderRadius: 6,
          border: '1px solid #e8e8e8',
        }}
        bodyStyle={{ padding: '12px 20px' }}
      >
        <Space size={16} wrap>
          <Space size={4}>
            <FilterOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
            <Text type="secondary" style={{ fontSize: 12 }}>Фильтры:</Text>
          </Space>
          <Select
            placeholder="Статус"
            allowClear
            style={{ width: 180 }}
            size="small"
            value={statusFilter}
            onChange={(val) => setStatusFilter(val)}
            options={[
              { value: 'pending', label: 'Ожидание' },
              { value: 'pre_approved', label: 'Предодобрен' },
              { value: 'active', label: 'Одобрен' },
              { value: 'inactive', label: 'Неактивен' },
            ]}
          />
          <Space size={4}>
            <Text type="secondary" style={{ fontSize: 12 }}>AI балл:</Text>
            <InputNumber
              placeholder="от"
              size="small"
              min={0}
              max={100}
              style={{ width: 65 }}
              value={aiScoreMin}
              onChange={(val) => setAiScoreMin(val ?? undefined)}
            />
            <Text type="secondary">—</Text>
            <InputNumber
              placeholder="до"
              size="small"
              min={0}
              max={100}
              style={{ width: 65 }}
              value={aiScoreMax}
              onChange={(val) => setAiScoreMax(val ?? undefined)}
            />
            <Button
              size="small"
              type="primary"
              onClick={() => setFilterVersion(v => v + 1)}
            >
              Применить
            </Button>
          </Space>
          {(statusFilter || aiScoreMin !== undefined || aiScoreMax !== undefined) && (
            <Button
              size="small"
              icon={<ClearOutlined />}
              onClick={() => {
                setStatusFilter(undefined)
                setAiScoreMin(undefined)
                setAiScoreMax(undefined)
                setFilterVersion(v => v + 1)
              }}
            >
              Сбросить
            </Button>
          )}
        </Space>
      </Card>

      {/* * Table */}
      <Card
        style={{
          borderRadius: 6,
          border: '1px solid #e8e8e8',
        }}
        bodyStyle={{ padding: 0 }}
      >
        <Table
          columns={columns}
          dataSource={sources}
          loading={loading}
          pagination={{
            ...pagination,
            total: domainSourcesCount,
            showSizeChanger: true,
            showTotal: (total: number) => `Всего: ${total}`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
          rowKey="id"
          size="small"
          sortDirections={['ascend', 'descend']}
        />
      </Card>
      
      {/* * Admin Status Edit Modal */}
      <Modal
        title={
          <Space>
            <ToolOutlined />
            <span>Изменить статус источника (Руководитель)</span>
          </Space>
        }
        open={adminModalVisible}
        onCancel={() => {
          setAdminModalVisible(false)
          setAdminModalSource(null)
          adminStatusForm.resetFields()
        }}
        onOk={handleAdminStatusSubmit}
        confirmLoading={updatingAdminStatus}
        okText="Применить"
        cancelText="Отмена"
        width={600}
      >
        {adminModalSource && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Text strong>Источник:</Text> <Tag>{adminModalSource.source}</Tag>
              <Text type="secondary" style={{ marginLeft: 8 }}>
                Текущий статус: <Tag color={statusColors[adminModalSource.status]}>{statusLabels[adminModalSource.status]}</Tag>
              </Text>
            </div>
            
            <Form
              form={adminStatusForm}
              layout="vertical"
            >
              <Form.Item
                name="status"
                label="Новый статус"
                rules={[{ required: true, message: 'Выберите статус' }]}
              >
                <Select
                  placeholder="Выберите статус"
                  options={[
                    { value: 'pending', label: 'Ожидание' },
                    { value: 'pre_approved', label: 'Предварительно одобрен' },
                    { value: 'active', label: 'Одобрен' },
                    { value: 'inactive', label: 'Неактивен' },
                    { value: 'deleted', label: 'Удален' },
                  ]}
                />
              </Form.Item>
              
              <Form.Item
                name="comment"
                label="Комментарий"
                rules={[{ required: true, message: 'Укажите причину изменения статуса' }]}
              >
                <TextArea
                  rows={4}
                  placeholder="Например: Исправление ошибки, источник kreditnykomissar.ru был неправильно обработан"
                  maxLength={1000}
                  showCount
                />
              </Form.Item>
            </Form>
            
            <div style={{ 
              padding: '8px 12px', 
              background: '#fff7e6', 
              border: '1px solid #ffd591',
              borderRadius: '4px'
            }}>
              <Text type="warning" style={{ fontSize: '12px' }}>
                ⚠️ Это действие будет записано в историю статусов с отметкой "[ADMIN MANUAL OVERRIDE]"
              </Text>
            </div>
          </Space>
        )}
      </Modal>
    </div>
  )
})

// * Display name for debugging
DomainsListTab.displayName = 'DomainsListTab'

export default DomainsListTab

